// src/admin/pages/RunListPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminTable from '../components/AdminTable.jsx';
import AdminPagination from '../components/AdminPagination.jsx';
import StatusBadge from '../components/StatusBadge.jsx';
import RunFilters from '../components/RunFilters.jsx';
import { listRuns } from '../runsApi.js';
import { adminListTenants } from '../adminApi.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import { COLORS, FONT_MONO, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

const LIMIT = 20;

function sevenDaysAgoIso() {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10); // YYYY-MM-DD
}

export default function RunListPage() {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const { isSuperadmin, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const offset = parseInt(searchParams.get('offset') || '0', 10);
  const urlTenant = searchParams.get('tenant') || '';
  const statusFromUrl = (searchParams.get('status') || '').split(',').filter(Boolean);
  // Default `since` to 7 days ago only when URL has no `since` param.
  const urlSince = searchParams.get('since') || sevenDaysAgoIso();
  const urlBefore = searchParams.get('before') || '';
  const urlUser = searchParams.get('user') || '';
  const urlPlatform = searchParams.get('platform') || '';
  const urlFormat = searchParams.get('content_format') || '';
  const hasActiveFilters =
    statusFromUrl.length > 0 ||
    !!urlUser || !!urlPlatform || !!urlFormat ||
    (searchParams.get('since') || '') !== '' ||
    (searchParams.get('before') || '') !== '';

  function patchParams(patch) {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === '' || v == null) next.delete(k);
      else next.set(k, String(v));
    });
    setSearchParams(next, { replace: false });
  }

  const tenantStorageKey = `runs_last_tenant:${user?.user_id || user?.email || 'anon'}`;

  const [runs, setRuns] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    if (!isSuperadmin) return;
    adminListTenants().then(setTenants).catch(() => setTenants([]));
  }, [isSuperadmin]);

  // Persist current tenant; hydrate URL from last-used on first mount when empty.
  useEffect(() => {
    if (!isSuperadmin) return;
    if (urlTenant) {
      try { localStorage.setItem(tenantStorageKey, urlTenant); } catch {}
      return;
    }
    try {
      const last = localStorage.getItem(tenantStorageKey);
      if (last) patchParams({ tenant: last });
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperadmin, urlTenant, tenantStorageKey]);

  const load = useCallback(async () => {
    if (isSuperadmin && !urlTenant) {
      setRuns([]); setTotal(0); setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await listRuns({
        tenant: urlTenant || undefined,
        status: statusFromUrl.length > 0 ? statusFromUrl : undefined,
        since: urlSince || undefined,
        before: urlBefore || undefined,
        user: urlUser || undefined,
        platform: urlPlatform || undefined,
        contentFormat: urlFormat || undefined,
        limit: LIMIT,
        offset,
      });
      setRuns(data.runs || []);
      setTotal(typeof data.total === 'number' ? data.total : (data.runs?.length || 0));
    } catch (err) {
      const msg = err.message || '';
      // Stale/invalid saved tenant → BE 403. Clear it so the next mount lets
      // the user pick a fresh tenant from the dropdown instead of looping.
      if (isSuperadmin && urlTenant && /403/.test(msg)) {
        try { localStorage.removeItem(tenantStorageKey); } catch {}
        setError(t('runList.tenantInaccessible', { tenant: urlTenant }));
        patchParams({ tenant: '' });
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperadmin, urlTenant, urlSince, urlBefore, urlUser, urlPlatform, urlFormat, offset, statusFromUrl.join(',')]);

  useEffect(() => { load(); }, [load]);

  const columns = [
    {
      key: 'topic', label: t('runList.columns.topic'),
      render: row => (
        <span title={row.topic || ''} style={{ display: 'inline-block', maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
          {row.topic || '—'}
        </span>
      ),
    },
    {
      key: 'platform', label: t('runList.columns.platformFormat'), width: '180px',
      render: row => `${row.platform || '—'}${row.content_format ? ` / ${row.content_format}` : ''}`,
    },
    { key: 'status', label: t('shared.status'), width: '120px', render: row => <StatusBadge status={row.status} /> },
    {
      key: 'user_email', label: t('runList.columns.user'), width: '180px',
      render: row => row.created_by_email || row.user_email || row.user_id || '—',
    },
    ...(isSuperadmin ? [{
      key: 'tenant', label: t('shared.tenant'), width: '160px',
      render: row => row.tenant_display_name || row.tenant_id || '—',
    }] : []),
    {
      key: 'created_at', label: t('runList.columns.created'), width: '140px',
      render: row => row.created_at
        ? new Date(row.created_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })
        : '—',
    },
    {
      key: 'task_id', label: t('runList.columns.taskId'), width: '110px',
      render: row => row.task_id ? (
        <code style={{ fontFamily: FONT_MONO, fontSize: '11px' }}>
          {String(row.task_id).slice(-8)}
        </code>
      ) : '—',
    },
  ];

  return (
    <div style={{ padding: '32px 40px', fontFamily: FONT_SANS, color: COLORS.ink, maxWidth: '1200px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>{t('runList.title')}</h1>
        <p style={{ color: COLORS.textSecondary, fontSize: '13px', margin: '4px 0 0' }}>
          {t('runList.subtitle')}
        </p>
      </div>

      {error && (
        <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: RADII.controlInner, background: SEMANTIC.errore.bg, border: `1px solid ${SEMANTIC.errore.bg}`, color: SEMANTIC.errore.fg, fontSize: '13px' }}>
          {error}
        </div>
      )}

      {isSuperadmin && (
        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <label style={{ fontSize: '13px', color: COLORS.ink }}>{t('shared.tenantLabel')}</label>
          <select
            value={urlTenant}
            onChange={e => patchParams({ tenant: e.target.value, offset: '0' })}
            style={{
              padding: '6px 10px', borderRadius: RADII.controlInner,
              border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
              fontSize: '13px', fontFamily: FONT_SANS,
              minWidth: '240px',
            }}
          >
            <option value="">{t('shared.selectPlaceholder')}</option>
            {tenants.map(tn => (
              <option key={tn.id} value={tn.id}>{tn.display_name || tn.id}</option>
            ))}
          </select>
        </div>
      )}

      {isSuperadmin && !urlTenant ? (
        <div style={{ padding: '60px 40px', textAlign: 'center', background: COLORS.surface, borderRadius: RADII.control, border: `1px solid ${COLORS.border}`, color: COLORS.textSecondary, fontSize: '13px' }}>
          {t('runList.selectTenantPrompt')}
        </div>
      ) : (
        <>
          <RunFilters
            status={statusFromUrl}
            since={urlSince}
            before={urlBefore}
            user={urlUser}
            platform={urlPlatform}
            contentFormat={urlFormat}
            onChange={(patch) => patchParams({ ...patch, offset: '0' })}
          />

          {hasActiveFilters && (
            <button
              type="button"
              onClick={() => {
                const next = new URLSearchParams();
                if (urlTenant) next.set('tenant', urlTenant);
                setSearchParams(next, { replace: false });
              }}
              style={{
                padding: '5px 10px', borderRadius: RADII.controlInner, border: `1px solid ${COLORS.borderStrong}`,
                background: COLORS.surface, color: COLORS.ink, fontSize: '12px', cursor: 'pointer',
                fontFamily: FONT_SANS, marginBottom: '12px',
              }}
            >
              {t('runList.resetFilters')}
            </button>
          )}

          <div style={{ background: COLORS.surface, borderRadius: RADII.control, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
            <AdminTable
              columns={columns}
              rows={runs}
              loading={loading}
              emptyMessage={hasActiveFilters ? t('runList.emptyFiltered') : t('runList.emptyDefault')}
              onRowClick={row => navigate(`/admin/runs/${row.task_id}`, { state: { from: `/admin/runs?${searchParams}` } })}
              getRowKey={row => row.task_id}
            />
          </div>
          <AdminPagination total={total} limit={LIMIT} offset={offset} onChange={n => patchParams({ offset: n })} itemLabel="run" />
        </>
      )}
    </div>
  );
}
